import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { Box, Typography, Button, Chip } from "@mui/material";
import { SitesGrid } from "@coremind/site-template-landing";
import { useSiteMemberships } from "@coremind/site-template-landing";
import { useAuth } from "@coremind/identity-core";
import { useNavigate } from "react-router-dom";
/**
 * The www site is the "app store" for the Offsetmind platform.
 * Each card shows a site's membership model and whether the signed-in user has joined it.
 * Sites share the same Cognito user pool — sign in once, see your joined sites across the platform.
 */
const PLATFORM_SITES = [
    {
        slug: "hearthbook",
        label: "Hearthbook",
        description: "Family finance management and church ledger — budgets, giving reports, fund tracking, and statement sharing.",
        hostname: "www.offsetmind.com/hearthbook",
        membershipModel: "stripe",
        tags: ["Finance", "Church", "Personal"],
        accentColor: "#f59e0b",
    },
    {
        slug: "karqo",
        label: "Karqo",
        description: "Inventory and space management for households, teams, and small businesses. Scan, track, and organise everything you own.",
        hostname: "www.offsetmind.com/karqo",
        membershipModel: "free",
        tags: ["Inventory", "Organisation"],
        accentColor: "#22c55e",
    },
    {
        slug: "huntr",
        label: "Huntr",
        description: "Job search tracker — applications, contacts, interviews, offers, and follow-ups in one view. Never lose track of where you applied.",
        hostname: "www.offsetmind.com/huntr",
        membershipModel: "stripe",
        tags: ["Jobs", "Career"],
        accentColor: "#6366f1",
    },
    {
        slug: "bidbox",
        label: "Bidbox",
        description: "Multi-seller auction marketplace — browse and bid on live auctions, or catalog inventory and run your own sale as a seller.",
        hostname: "www.offsetmind.com/bidbox",
        membershipModel: "free",
        tags: ["Auctions", "Marketplace"],
        accentColor: "#3B5BDB",
    },
];
function HeroSection() {
    const { session } = useAuth();
    const navigate = useNavigate();
    return (_jsxs(Box, { sx: {
            py: { xs: 10, md: 14 },
            px: 3,
            textAlign: "center",
            background: "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(99,102,241,0.15), transparent)",
        }, children: [_jsx(Chip, { label: "One platform. Many sites.", size: "small", sx: { mb: 3, bgcolor: "rgba(99,102,241,0.12)", color: "#818cf8", border: "1px solid rgba(99,102,241,0.3)", fontSize: 12 } }), _jsxs(Typography, { variant: "h2", fontWeight: 800, sx: {
                    fontSize: { xs: "2rem", md: "3.25rem" },
                    letterSpacing: "-2px",
                    lineHeight: 1.1,
                    mb: 3,
                    background: "linear-gradient(135deg, #f1f5f9 30%, #94a3b8)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                }, children: ["Your apps, unified under", _jsx("br", {}), "one login"] }), _jsx(Typography, { color: "text.secondary", sx: { maxWidth: 520, mx: "auto", mb: 4, fontSize: "1.1rem", lineHeight: 1.7 }, children: "Offsetmind runs a suite of tools on a shared platform. Sign in once with your Offsetmind account and access every site you've joined." }), _jsx(Box, { sx: { display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }, children: session ? (_jsx(Button, { variant: "contained", size: "large", sx: { fontWeight: 700, px: 4 }, onClick: () => document.getElementById("sites")?.scrollIntoView({ behavior: "smooth" }), children: "View your sites" })) : (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "contained", size: "large", sx: { fontWeight: 700, px: 4 }, onClick: () => navigate("/signin"), children: "Sign in" }), _jsx(Button, { variant: "outlined", size: "large", sx: { fontWeight: 600, px: 4, borderColor: "rgba(255,255,255,0.2)", color: "inherit" }, onClick: () => document.getElementById("sites")?.scrollIntoView({ behavior: "smooth" }), children: "Learn more" })] })) })] }));
}
function PricingSection() {
    return (_jsx(Box, { id: "pricing", sx: { py: 10, px: 3, bgcolor: "background.paper", borderTop: "1px solid", borderColor: "divider" }, children: _jsxs(Box, { sx: { maxWidth: 1100, mx: "auto", textAlign: "center" }, children: [_jsx(Typography, { variant: "overline", sx: { color: "primary.light", letterSpacing: 3, fontWeight: 700 }, children: "Pricing" }), _jsx(Typography, { variant: "h4", fontWeight: 700, mt: 1, mb: 2, children: "Simple, transparent pricing" }), _jsx(Typography, { color: "text.secondary", mb: 6, children: "Each site has its own model \u2014 some are free, some are subscription-based, some accept donations." }), _jsx(Box, { sx: { display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2 }, children: [
                        { label: "Free", desc: "No payment required. Join and use immediately.", color: "#22c55e" },
                        { label: "Pro (Stripe)", desc: "Monthly or annual subscription charged via Stripe.", color: "#818cf8" },
                        { label: "Donation-based", desc: "Pay what you can. No minimum, no gate.", color: "#fbbf24" },
                        { label: "In-app purchases", desc: "Free to join; unlock premium features inside the app.", color: "#06b6d4" },
                    ].map(({ label, desc, color }) => (_jsxs(Box, { sx: { p: 2.5, borderRadius: 2, border: `1px solid ${color}30`, bgcolor: `${color}08` }, children: [_jsx(Typography, { fontWeight: 700, sx: { color, mb: 0.75 }, children: label }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: desc })] }, label))) })] }) }));
}
export default function HomePage() {
    const { session } = useAuth();
    const slugs = PLATFORM_SITES.map((s) => s.slug);
    const { memberships, membershipModels, loading } = useSiteMemberships(slugs);
    const sites = PLATFORM_SITES.map((s) => ({
        ...s,
        membershipModel: (membershipModels[s.slug] ?? s.membershipModel),
        joined: loading ? undefined : memberships[s.slug],
    }));
    return (_jsxs(Box, { children: [_jsx(HeroSection, {}), _jsx(Box, { id: "sites", sx: { py: 10, px: 3 }, children: _jsxs(Box, { sx: { maxWidth: 1100, mx: "auto" }, children: [_jsx(Typography, { variant: "overline", sx: { color: "primary.light", letterSpacing: 3, fontWeight: 700 }, children: "Platform sites" }), _jsx(Typography, { variant: "h4", fontWeight: 700, mt: 1, mb: 1, children: "All the tools you need" }), _jsx(Typography, { color: "text.secondary", mb: 4, children: session ? "Signed in — your joined sites show a badge." : "Sign in to see which sites you've already joined." }), _jsx(SitesGrid, { sites: sites, membershipLoading: loading })] }) }), _jsx(PricingSection, {})] }));
}
